import { useState, useEffect } from "react";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";

type ImportedUserRow = {
    firstName: string;
    lastName: string;
    title: string;
    supervisor: string;
    location: string;
    email: string;
};


export default function ImportUsersModal(props){
    const { isOpen, onClose, companyId, updateSpinner } = props;
    const [userData, updateUserData] = useState<ImportedUserRow[]>([]);
    const [isImporting, setIsImporting] = useState(false);

    // Reset data when modal opens
    useEffect(() => {
        if (isOpen) {
            updateUserData([]);
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    function handleClose() {
        if (!isImporting) {
            updateUserData([]);
            onClose();
        }
    }
    function OnNewUserData(e) {
        const data = e.target.value;

        // Split the data into lines, handling different newline formats (\n, \r\n)
        const lines = data.split(/\r?\n/).map(line => line.trim()).filter(line => line.length > 0);

        // If there are no lines or only one line (just the header), set users to an empty array
        if (lines.length <= 1) {
            updateUserData([]);
            return;
        }

        const headerRow = lines[0].split("\t").map(header => header.trim());
        let firstIndex = headerRow.findIndex(header =>
            header.toLowerCase().includes("fname") || header.toLowerCase().includes("first") || header.toLowerCase().includes("given")
        );
        let lastIndex = headerRow.findIndex(header =>
            header.toLowerCase().includes("lname") || header.toLowerCase().includes("last") || header.toLowerCase().includes("family") || header.toLowerCase().includes("surname")
        );
        let emailIndex = headerRow.findIndex(header => header.toLowerCase().includes("email"));
        let titleIndex = headerRow.findIndex(header => 
            header.toLowerCase().includes("title") || header.toLowerCase().includes("position") || header.toLowerCase().includes("job")|| header.toLowerCase().includes("role"));
        let locationIndex = headerRow.findIndex(header =>
            header.toLowerCase().includes("location") || header.toLowerCase().includes("site"));
        let supervisorIndex = headerRow.findIndex(header => 
            header.toLowerCase().includes("supervisor")|| header.toLowerCase().includes("manager"));

        // Skip the first line (header) and process the remaining lines as user data
        const users = lines.slice(1).map(line => {
            const parts = line.split("\t"); // Split by tab character
            const ret: Partial<ImportedUserRow> = {};
            if (firstIndex >= 0)
                ret.firstName = parts[firstIndex]?.trim() || "";
            if (lastIndex >= 0)
                ret.lastName = parts[lastIndex]?.trim() || "";
            if(titleIndex>=0)
                ret.title = parts[titleIndex]?.trim() || "";
            if(supervisorIndex>=0)
                ret.supervisor = parts[supervisorIndex]?.trim() || "";
            if(locationIndex>=0)
                ret.location = parts[locationIndex]?.trim() || "";
            if(emailIndex>=0)
                ret.email = parts[emailIndex]?.trim() || "";
            return {
                firstName: ret.firstName || "",
                lastName: ret.lastName || "",
                title: ret.title || "",
                supervisor: ret.supervisor || "",
                location: ret.location || "",
                email: ret.email || "",
            };
        });

        // Update the user data state
        updateUserData(users);
    }
    async function onImport() {
        setIsImporting(true);
        if (updateSpinner) updateSpinner(true);
        
        try {
            const userDataToImport = userData.map(user => {
                return {
                    firstName: user.firstName,
                    lastName: user.lastName,
                    title: user.title,
                    supervisor: user.supervisor,
                    location: user.location,
                    email: user.email,
                };
            });
            const data = { companyId: companyId, users: userDataToImport };
            await RightTimeApi.postAsync("/users", data);
            updateUserData([]);
            onClose();
        } catch (error) {
            console.error('Error importing users:', error);
        } finally {
            setIsImporting(false);
            if (updateSpinner) updateSpinner(false);
        }
    }
    
    const footer = userData.length > 0 && (
        <div>
            <button className="btn" onClick={() => updateUserData([])} disabled={isImporting}>
                Restart
            </button>
            <button className="btn" onClick={onImport} disabled={isImporting}>
                {isImporting ? 'Importing...' : 'Import'}
            </button>
            <button className="btn" onClick={handleClose} disabled={isImporting}>
                Cancel
            </button>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            title="Import Users"
            onClose={handleClose}
            footer={footer}
            className="importUsersModal"
            updateSpinner={updateSpinner}
        >
                    <div>
                        {userData.length === 0 ?
                            <div>
                                <p>Copy the user data (with captions) into the box below.</p>
                                <textarea placeholder="paste data here..." onChange={OnNewUserData} />
                            </div>
                            : <div>
                                <p>If the following data is correct, press "Import" below:</p>
                                <p>If the following is not correct, fix your spreadsheet, click "Restart" and import the data again</p>
                                <div className="tableWrapper dimensionTable scrollable horizontal">
                                    <table className="block notSelectable">
                                        <caption>
                                          <span className="title">Users</span>
                                        </caption>
                                        <thead>
                                            <tr>
                                                <th>First Name</th>
                                                <th>Last Name</th>
                                                <th>Email</th>
                                                <th>Title</th>
                                                <th>Reporting Manager</th>
                                                <th>Location</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {userData &&
                                                userData.map((u, i) => {
                                                    return (
                                                        <tr key={"user" + i}>
                                                            <td>{u.firstName}</td>
                                                            <td>{u.lastName}</td>
                                                            <td>{u.email}</td>
                                                            <td>{u.title}</td>
                                                            <td>{u.supervisor}</td>
                                                            <td>{u.location}</td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        }
                    </div>
        </Modal>
    );
}


